import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  type MutableRefObject,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { getSocket } from '@/hooks/useRealtimeSync';
import { api, ApiError } from '@/api/client';
import { dismissAgentChatNotifications } from '@/notifications/notificationOrchestrator';
import type { TrackerChatMessage } from '@/components/Messenger/types/chatMessageTypes';
import {
  filterAgentChatMessagesForDbBackedTimeline,
  projectAgentChatHistoricalFeed,
} from '@/components/Messenger/agent/agentChatThreadProjection';
import { isValidAgentModelSlot } from '@/components/Messenger/agent/assistantChatMessageMetadata';
import {
  collectPersistedAssistantIds,
  mapAgentApiMessageToUiMessage,
} from '@/components/Messenger/agent/agentChatMessageMap';
import { mergeStableTrackerChatMessages } from '@/components/Messenger/agent/agentChatMessageStability';
import {
  agentChatApplyServerSocketPatch,
  agentChatReplaceAllFromApi,
  agentChatReset,
  getAgentChatSnapshot,
  parseAgentChatServerSocketPatch,
} from '@/components/Messenger/cache/agentChatCacheStore';
import {
  MESSENGER_AGENT_CHAT_RECOVER,
  type MessengerAgentChatRecoverDetail,
} from '@/constants/messengerEvents';

export type UseAgentChatThreadParams = {
  serverChatId: string | null;
  uiSessionId: string;
  paneActive: boolean;
  messengerSurfaceVisible: boolean;
  liveStreamActive: boolean;
  messages: TrackerChatMessage[];
  setMessages: Dispatch<SetStateAction<TrackerChatMessage[]>>;
  status: string;
  skipHistoryLoadOnceRef: MutableRefObject<boolean>;
  lastRetryPayloadRef: MutableRefObject<{ parts: TrackerChatMessage['parts'] } | null>;
  activeStreamChatIdRef: MutableRefObject<string | null>;
  onProcessingStateChange?: (chatId: string, processing: boolean) => void;
  onServerChatAccessLost?: () => void;
  onRefreshAgentChatsList?: () => void;
  showError: (message: string) => void;
  resetLiveStream: (chatId?: string | null) => void;
};

export type UseAgentChatThreadResult = {
  historicalMessages: TrackerChatMessage[];
  historyLoading: boolean;
  messagesRef: MutableRefObject<TrackerChatMessage[]>;
  refreshPersistedChatMessages: (chatId: string) => Promise<void>;
  /** Локальный черновик без `serverChatId`: заменить тред и индекс ассистентов атомарно. */
  replaceLocalThread: (next: TrackerChatMessage[]) => void;
};

function hasCriticalAgentPatchGaps(patchMessages: unknown[]): boolean {
  for (const raw of patchMessages) {
    if (!raw || typeof raw !== 'object') continue;
    const row = raw as { role?: unknown; model_slot?: unknown; send_failed?: unknown };
    const role = typeof row.role === 'string' ? row.role : '';
    if (role === 'assistant') {
      if (!isValidAgentModelSlot(row.model_slot)) return true;
    }
    if (role === 'user') {
      if (typeof row.send_failed !== 'boolean') return true;
    }
  }
  return false;
}

/**
 * Слой данных агент-чата: загрузка истории, сокет-патчи, стабильный мерж с `useChat`,
 * проекция ленты и read-receipt. `AgentChatView` остаётся композицией UI и транспорта.
 */
export function useAgentChatThread(params: UseAgentChatThreadParams): UseAgentChatThreadResult {
  const {
    serverChatId,
    uiSessionId,
    paneActive,
    messengerSurfaceVisible,
    liveStreamActive,
    messages,
    setMessages,
    status,
    skipHistoryLoadOnceRef,
    lastRetryPayloadRef,
    activeStreamChatIdRef,
    onProcessingStateChange,
    onServerChatAccessLost,
    onRefreshAgentChatsList,
    showError,
    resetLiveStream,
  } = params;

  const serverChatIdRef = useRef(serverChatId);
  serverChatIdRef.current = serverChatId;

  const paneActiveRef = useRef(paneActive);
  paneActiveRef.current = paneActive;

  const lastMarkedReadUiMessageIdRef = useRef<string | null>(null);
  const pendingServerRefreshChatIdRef = useRef<string | null>(null);
  const refreshPersistedChatMessagesRef = useRef<((chatId: string) => Promise<void>) | null>(null);
  const fallbackRefreshInFlightByChatRef = useRef<Record<string, true>>({});
  const fallbackRefreshLastAtByChatRef = useRef<Record<string, number>>({});
  const [historyLoading, setHistoryLoading] = useState(false);
  const [persistedAssistantIds, setPersistedAssistantIds] = useState<Record<string, true>>({});
  const stableDbBackedMessagesRef = useRef<TrackerChatMessage[]>([]);

  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  useEffect(() => {
    setPersistedAssistantIds({});
    lastMarkedReadUiMessageIdRef.current = null;
    stableDbBackedMessagesRef.current = [];
  }, [uiSessionId]);

  useEffect(() => {
    if (status === 'ready') {
      const completedChatId = activeStreamChatIdRef.current?.trim() || '';
      const currentChatId = serverChatIdRef.current?.trim() || '';
      if (completedChatId && completedChatId === currentChatId) {
        onProcessingStateChange?.(completedChatId, false);
        activeStreamChatIdRef.current = null;
        const last = messagesRef.current[messagesRef.current.length - 1];
        const needsRecoveryRefresh = last?.role === 'user' && !last.metadata?.sendFailed;
        if (needsRecoveryRefresh) {
          const timer = window.setTimeout(() => {
            const refresh = refreshPersistedChatMessagesRef.current;
            if (refresh) void refresh(completedChatId);
          }, 250);
          return () => window.clearTimeout(timer);
        }
      }
    }
  }, [status, onProcessingStateChange, activeStreamChatIdRef]);

  useLayoutEffect(() => {
    if (!serverChatId) {
      setHistoryLoading(false);
      setPersistedAssistantIds({});
      stableDbBackedMessagesRef.current = [];
      return;
    }
    if (skipHistoryLoadOnceRef.current) {
      skipHistoryLoadOnceRef.current = false;
      return;
    }
    let cancelled = false;
    setHistoryLoading(true);
    void api.agentChats
      .messages(serverChatId, { limit: 100 })
      .then((res) => {
        if (cancelled) return;
        const ui: TrackerChatMessage[] = res.messages.map(mapAgentApiMessageToUiMessage);
        let lastFailedParts: TrackerChatMessage['parts'] | null = null;
        for (let i = ui.length - 1; i >= 0; i -= 1) {
          const row = ui[i];
          if (row.role === 'user' && row.metadata?.sendFailed) {
            lastFailedParts = row.parts;
            break;
          }
        }
        if (lastFailedParts) {
          lastRetryPayloadRef.current = { parts: lastFailedParts };
        } else {
          lastRetryPayloadRef.current = null;
        }
        onProcessingStateChange?.(serverChatId, Boolean(res.processing));
        lastMarkedReadUiMessageIdRef.current = res.last_read_message_id ?? null;
        const merged = mergeStableTrackerChatMessages(messagesRef.current, ui);
        setMessages(merged);
        const persisted = collectPersistedAssistantIds(merged);
        setPersistedAssistantIds(persisted);
        agentChatReplaceAllFromApi(serverChatId, merged, {
          lastReadUiMessageId: res.last_read_message_id ?? null,
          processing: Boolean(res.processing),
          processingPhase: res.processing_phase ?? null,
        });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setMessages([]);
        setPersistedAssistantIds({});
        const notFound =
          (e instanceof ApiError && e.status === 404) ||
          (e instanceof Error && e.message === 'Чат не найден');
        if (notFound) {
          showError('Чат не найден или удалён. Открыт новый черновик.');
          agentChatReset(serverChatId);
          onServerChatAccessLost?.();
        }
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    serverChatId,
    setMessages,
    onServerChatAccessLost,
    showError,
    onProcessingStateChange,
    skipHistoryLoadOnceRef,
    lastRetryPayloadRef,
  ]);

  const refreshPersistedChatMessages = useCallback(
    (chatId: string) => {
      return api.agentChats
        .messages(chatId, { limit: 100 })
        .then((res) => {
          const ui: TrackerChatMessage[] = res.messages.map(mapAgentApiMessageToUiMessage);
          onProcessingStateChange?.(chatId, Boolean(res.processing));
          lastMarkedReadUiMessageIdRef.current = res.last_read_message_id ?? null;
          const merged = mergeStableTrackerChatMessages(messagesRef.current, ui);
          setMessages(merged);
          const persisted = collectPersistedAssistantIds(merged);
          setPersistedAssistantIds(persisted);
          agentChatReplaceAllFromApi(chatId, merged, {
            lastReadUiMessageId: res.last_read_message_id ?? null,
            processing: Boolean(res.processing),
            processingPhase: res.processing_phase ?? null,
          });
        })
        .catch((e: unknown) => {
          const notFound =
            (e instanceof ApiError && e.status === 404) ||
            (e instanceof Error && e.message === 'Чат не найден');
          if (notFound) {
            showError('Чат не найден или удалён. Открыт новый черновик.');
            agentChatReset(chatId);
            onServerChatAccessLost?.();
          }
        });
    },
    [setMessages, onServerChatAccessLost, showError, onProcessingStateChange]
  );

  const scheduleFallbackRefreshPersistedChatMessages = useCallback(
    (chatId: string) => {
      const id = chatId.trim();
      if (!id) return;
      const now = Date.now();
      const inFlight = Boolean(fallbackRefreshInFlightByChatRef.current[id]);
      if (inFlight) return;
      const lastAt = fallbackRefreshLastAtByChatRef.current[id] ?? 0;
      if (now - lastAt < 1200) return;
      fallbackRefreshInFlightByChatRef.current[id] = true;
      fallbackRefreshLastAtByChatRef.current[id] = now;
      void refreshPersistedChatMessages(id).finally(() => {
        delete fallbackRefreshInFlightByChatRef.current[id];
        fallbackRefreshLastAtByChatRef.current[id] = Date.now();
      });
    },
    [refreshPersistedChatMessages]
  );

  useEffect(() => {
    refreshPersistedChatMessagesRef.current = refreshPersistedChatMessages;
  }, [refreshPersistedChatMessages]);

  useEffect(() => {
    const onRecover = (e: Event) => {
      const detail = (e as CustomEvent<MessengerAgentChatRecoverDetail>).detail;
      const chatId = detail?.chatId?.trim() || '';
      if (!chatId || chatId !== serverChatIdRef.current) return;
      const snap = getAgentChatSnapshot(chatId);
      if (snap.hydrated) {
        setMessages((prev) => mergeStableTrackerChatMessages(prev, snap.messages));
        setPersistedAssistantIds(snap.persistedAssistantIds);
        lastMarkedReadUiMessageIdRef.current = snap.lastReadUiMessageId;
        onProcessingStateChange?.(chatId, snap.processing);
        if (!snap.processing) {
          resetLiveStream(chatId);
        }
        return;
      }
      const refresh = refreshPersistedChatMessagesRef.current;
      if (refresh) void refresh(chatId);
    };
    window.addEventListener(MESSENGER_AGENT_CHAT_RECOVER, onRecover);
    return () => {
      window.removeEventListener(MESSENGER_AGENT_CHAT_RECOVER, onRecover);
    };
  }, [onProcessingStateChange, resetLiveStream, setMessages]);

  useEffect(() => {
    const socket = getSocket();
    const onAgentChatMessage = (raw: unknown) => {
      if (!raw || typeof raw !== 'object') return;
      const chatIdRaw = (raw as { chat_id?: unknown }).chat_id;
      if (typeof chatIdRaw !== 'string' || !chatIdRaw.trim()) return;
      const chatId = chatIdRaw.trim();
      if (!serverChatIdRef.current || chatId !== serverChatIdRef.current) return;
      const patch = parseAgentChatServerSocketPatch(raw);
      const snap = getAgentChatSnapshot(chatId);
      const prevForPatch =
        snap.hydrated && snap.messages.length > 0 ? snap.messages : messagesRef.current;
      const applied = patch ? agentChatApplyServerSocketPatch(chatId, prevForPatch, patch) : null;
      if (!paneActiveRef.current) {
        // Even when patch was successfully applied into cache while pane is inactive,
        // we must force UI sync on return to this chat.
        pendingServerRefreshChatIdRef.current = chatId;
        return;
      }
      if (applied) {
        setMessages(applied.messages);
        setPersistedAssistantIds(applied.persistedAssistantIds);
        lastMarkedReadUiMessageIdRef.current = applied.lastReadUiMessageId;
        onProcessingStateChange?.(chatId, applied.processing);
        if (patch?.messages && hasCriticalAgentPatchGaps(patch.messages)) {
          scheduleFallbackRefreshPersistedChatMessages(chatId);
        }
        if (!applied.processing) {
          resetLiveStream(chatId);
        }
        return;
      }
      scheduleFallbackRefreshPersistedChatMessages(chatId);
    };
    socket.on('messenger:agent_chat_message', onAgentChatMessage);
    return () => {
      socket.off('messenger:agent_chat_message', onAgentChatMessage);
    };
  }, [
    onProcessingStateChange,
    setMessages,
    setPersistedAssistantIds,
    resetLiveStream,
    scheduleFallbackRefreshPersistedChatMessages,
  ]);

  useEffect(() => {
    if (!paneActive || !serverChatId) return;
    if (pendingServerRefreshChatIdRef.current !== serverChatId) return;
    pendingServerRefreshChatIdRef.current = null;
    const snap = getAgentChatSnapshot(serverChatId);
    if (snap.hydrated && snap.messages.length > 0) {
      setMessages((prev) => mergeStableTrackerChatMessages(prev, snap.messages));
      setPersistedAssistantIds(snap.persistedAssistantIds);
      return;
    }
    void refreshPersistedChatMessages(serverChatId);
  }, [paneActive, serverChatId, refreshPersistedChatMessages, setMessages]);

  const dbBackedMessages = useMemo(() => {
    const filtered = filterAgentChatMessagesForDbBackedTimeline(messages, persistedAssistantIds);
    const prevStable = stableDbBackedMessagesRef.current;
    const merged = mergeStableTrackerChatMessages(prevStable, filtered);
    stableDbBackedMessagesRef.current = merged;
    return merged;
  }, [messages, persistedAssistantIds]);

  const historicalMessages = useMemo(
    () => projectAgentChatHistoricalFeed(dbBackedMessages, liveStreamActive, persistedAssistantIds),
    [dbBackedMessages, liveStreamActive, persistedAssistantIds]
  );

  useEffect(() => {
    if (!serverChatId || !paneActive || !messengerSurfaceVisible || historyLoading) return;
    let target: TrackerChatMessage | null = null;
    for (let i = dbBackedMessages.length - 1; i >= 0; i -= 1) {
      if (dbBackedMessages[i]?.role !== 'system') {
        target = dbBackedMessages[i] as TrackerChatMessage;
        break;
      }
    }
    if (!target?.id) return;
    if (lastMarkedReadUiMessageIdRef.current === target.id) return;
    lastMarkedReadUiMessageIdRef.current = target.id;
    void api.agentChats
      .read(serverChatId, target.id)
      .then(() => {
        dismissAgentChatNotifications(serverChatId);
        onRefreshAgentChatsList?.();
      })
      .catch(() => {
        if (lastMarkedReadUiMessageIdRef.current === target?.id) {
          lastMarkedReadUiMessageIdRef.current = null;
        }
      });
  }, [
    serverChatId,
    paneActive,
    messengerSurfaceVisible,
    historyLoading,
    dbBackedMessages,
    onRefreshAgentChatsList,
  ]);

  const replaceLocalThread = useCallback(
    (next: TrackerChatMessage[]) => {
      setMessages(next);
      setPersistedAssistantIds(collectPersistedAssistantIds(next));
    },
    [setMessages]
  );

  return {
    historicalMessages,
    historyLoading,
    messagesRef,
    refreshPersistedChatMessages,
    replaceLocalThread,
  };
}
